import React, { useEffect, useState } from 'react'
import CustomContainer from '../../components/common/CustomContainer'
import { Stack, Typography } from '@mui/material'
import { UserManager } from '../../services/api/UserManager'
import { UserDto, UserRole } from '../../services/api/sharedTypes/User'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import UserTable from './UserTable'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import AddUserDialog from './AddUserDialog'
import { getAuthState } from '../../services/redux/auth/auth-selectors'

const Administration: React.FC = () => {
    const [loadingData, setLoadingData] = useState<boolean>(true)
    const [loadingColumn, setLoadingColumn] = useState<number>(-1)
    const [userList, setUserList] = useState<UserDto[]>([])

    const dispatch = useAppDispatch()
    const { parsedToken } = useAppSelector(getAuthState)

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)
            try {
                const usersRes = await UserManager.getManager().getUsersAsync()
                if (usersRes) {
                    setUserList(usersRes.users.filter(user => user.id !== parsedToken.UserId))
                }
            } catch (error) {
                setLoadingData(false)
                dispatch(displayMessage({ message: error, type: 'error' }))
                return
            }
            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    const updateUser = async (userId: string, role: UserRole, index: number) => {
        try {
            setLoadingColumn(index)
            const userRes = await UserManager.getManager().updateUserAsync(userId, role)
            if (userRes) {
                setUserList(userList.map(user => user.id === userId ? userRes : user))
                dispatch(displayMessage({ message: 'Role změněna', type: 'success' }))
                setLoadingColumn(-1)
            }
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setLoadingColumn(-1)
            return
        }
    }

    const addUser = async (username: string, password: string, role: UserRole) => {
        try {
            setLoadingColumn(userList.length + 1)
            const userRes = await UserManager.getManager().createUserAsync(username, password, role)
            if (userRes) {
                setUserList([...userList, {
                    id: userRes.id,
                    createdAt: new Date(),
                    lastModifiedAt: new Date(),
                    role: userRes.role,
                    username: userRes.username
                }])
                dispatch(displayMessage({ message: 'Uživatel přidán', type: 'success' }))
                setLoadingColumn(-1)
            }
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setLoadingColumn(-1)
            return
        }
    }

    const deleteUser = async (userId: string, index: number) => {
        try {
            setLoadingColumn(index)
            await UserManager.getManager().deleteUserAsync(userId)
            setUserList(userList.filter(user => user.id !== userId))
            dispatch(displayMessage({ message: 'Uživatel smazán', type: 'success' }))
            setLoadingColumn(-1)
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setLoadingColumn(-1)
            return
        }
    }

    return (
        <CustomContainer>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='h5'>Administrace</Typography>
                <AddUserDialog onAddUser={addUser} />
            </Stack>

            {loadingData ? (
                <ScreenLoadingPlaceholder />
            ) :
                userList.length > 0 ? (
                    <UserTable userList={userList} onChange={updateUser} onDelete={deleteUser} loadingColumn={loadingColumn} />
                ) : (
                    <Typography variant='h6'>Nebyli nalezeni žádní uživatelé</Typography>
                )
            }
        </CustomContainer>
    )
}

export default Administration